import { useEffect, useMemo, useState } from "react";
import { PinIcon, StarIcon, ChevronRightIcon, SearchIcon } from "../site/icons.jsx";
import { lodgingNode, breadcrumbNode, itemListNode, useJsonLd, useHead, hawaiiPlace, absUrl, CONTEXT, WEBSITE_ID, ORG_ID } from "../lib/seo.js";

const IMAGE_ROOT = "https://images.aloharates.com";
const PAGE_SIZE = 24;

// slug → { name (matches properties.json `island`), tagline, hero, facts, intro }
const ISLANDS = {
  "oahu": {
    name: "Oahu",
    display: "Oʻahu",
    hero: "https://home.aloharates.com/home_page/oahu.webp",
    tagline: "The gathering place — Honolulu, Waikīkī, and the wild North Shore.",
    intro: "Home to about two-thirds of Hawaiʻi's population, Oʻahu balances the neon of Waikīkī and the memorials at Pearl Harbor with a rural North Shore known for winter big-wave surf. Most travelers fly into Daniel K. Inouye International (HNL) and stay along the south shore.",
    facts: [
      { label: "Airport", value: "HNL — Honolulu" },
      { label: "Best time", value: "Apr – Jun · Sep – Oct" },
      { label: "Avg high", value: "80–88°F year-round" },
      { label: "Known for", value: "Waikīkī, Diamond Head, Pearl Harbor, Pipeline" }
    ],
    areas: ["Waikīkī", "Ko Olina", "North Shore", "Kailua", "Downtown Honolulu"]
  },
  "maui": {
    name: "Maui",
    display: "Maui",
    hero: "https://home.aloharates.com/home_page/maui.webp",
    tagline: "The Valley Isle — resort coasts on one side, the Road to Hāna on the other.",
    intro: "Maui pairs the resort towns of Kāʻanapali and Wailea with volcanic Haleakalā and the winding Road to Hāna. Kahului Airport (OGG) is the main gateway. West Maui, South Maui, Upcountry, and Hāna each have distinct character — pick your base by the pace you want.",
    facts: [
      { label: "Airport", value: "OGG — Kahului" },
      { label: "Best time", value: "Apr – May · Sep – Nov" },
      { label: "Avg high", value: "78–87°F year-round" },
      { label: "Known for", value: "Road to Hāna, Haleakalā sunrise, humpback whales (Dec–Apr)" }
    ],
    areas: ["Kāʻanapali", "Wailea", "Lāhainā", "Kīhei", "Hāna", "Upcountry"]
  },
  "big-island": {
    name: "Big Island",
    display: "Hawaiʻi (Big Island)",
    hero: "https://home.aloharates.com/home_page/big_island.webp",
    tagline: "Active volcanoes, black-sand beaches, and eleven of the world's climate zones.",
    intro: "The Big Island is bigger than all the other Hawaiian islands combined. The dry Kona-Kohala coast holds the resort strips; Hilo on the east is lush and green. Hawaiʻi Volcanoes National Park is the anchor experience. Fly into Kona (KOA) for west-side stays or Hilo (ITO) for the east.",
    facts: [
      { label: "Airports", value: "KOA — Kona · ITO — Hilo" },
      { label: "Best time", value: "Apr – Sep for west coast · year-round for national park" },
      { label: "Avg high", value: "80–85°F (Kona) · 78–83°F (Hilo)" },
      { label: "Known for", value: "Kīlauea, Mauna Kea stargazing, Punaluʻu black sand" }
    ],
    areas: ["Kohala Coast", "Kailua-Kona", "Waikoloa", "Hilo", "Volcano"]
  },
  "kauai": {
    name: "Kauai",
    display: "Kauaʻi",
    hero: "https://home.aloharates.com/home_page/kauai.webp",
    tagline: "The Garden Isle — Nā Pali cliffs, Waimea Canyon, and rain-forest quiet.",
    intro: "Kauaʻi is the oldest and greenest of the main islands, with the dramatic Nā Pali coast, Waimea Canyon (the \"Grand Canyon of the Pacific\"), and the wet interior of Mount Waiʻaleʻale. Poʻipū is the sunniest resort base; Princeville and Hanalei anchor the north shore. Fly into Līhuʻe (LIH).",
    facts: [
      { label: "Airport", value: "LIH — Līhuʻe" },
      { label: "Best time", value: "Apr – Jun · Sep – Oct" },
      { label: "Avg high", value: "78–85°F year-round" },
      { label: "Known for", value: "Nā Pali Coast, Waimea Canyon, Hanalei Bay" }
    ],
    areas: ["Poʻipū", "Princeville", "Hanalei", "Kapaʻa", "Līhuʻe"]
  },
  "molokai": {
    name: "Molokai",
    display: "Molokaʻi",
    hero: "https://home.aloharates.com/home_page/molokai.webp",
    tagline: "The Friendly Isle — no traffic lights, no resorts, and Hawaiʻi's tallest sea cliffs.",
    intro: "Molokaʻi is the quietest of the main Hawaiian islands. There are no traffic lights and no building taller than a coconut tree. It's for travelers who want a slower Hawaiʻi — Kalaupapa's history, Pāpōhaku Beach's three miles of sand, and locally-run condos in Kaunakakai.",
    facts: [
      { label: "Airport", value: "MKK — Molokaʻi (Hoʻolehua)" },
      { label: "Best time", value: "Apr – Oct" },
      { label: "Avg high", value: "78–85°F year-round" },
      { label: "Known for", value: "Kalaupapa peninsula, Pāpōhaku Beach, ancient fishponds" }
    ],
    areas: ["Kaunakakai", "West End", "East End"]
  },
  "lanai": {
    name: "Lanai",
    display: "Lānaʻi",
    hero: "https://home.aloharates.com/home_page/lanai.webp",
    tagline: "Formerly the world's largest pineapple plantation — now a private, high-end escape.",
    intro: "Lānaʻi is small and largely owned by one person. Two Four Seasons resorts anchor tourism, along with a handful of vacation rentals. Most guests arrive by ferry from Lāhainā, Maui or on the short hop to Lānaʻi Airport (LNY). The Garden of the Gods, Hulopoʻe Bay, and Shipwreck Beach are the classics.",
    facts: [
      { label: "Airport", value: "LNY — Lānaʻi (or ferry from Lāhainā)" },
      { label: "Best time", value: "Apr – Oct" },
      { label: "Avg high", value: "78–84°F year-round" },
      { label: "Known for", value: "Hulopoʻe Bay, Garden of the Gods, spinner dolphins" }
    ],
    areas: ["Lānaʻi City", "Mānele Bay", "Kōʻele"]
  }
};

function propSlug(name) {
  return String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function propertyHref(p) {
  return `/properties/${p.id}-${propSlug(p.name)}`;
}

function PropertyCard({ property }) {
  return (
    <a href={propertyHref(property)} className="card overflow-hidden group flex flex-col">
      <div className="relative aspect-[4/3] overflow-hidden bg-sand-100">
        <img
          src={`${IMAGE_ROOT}/properties/${property.id}/0.webp`}
          alt={property.name}
          loading="lazy"
          className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
          onError={e => { e.currentTarget.style.display = "none"; }}
        />
      </div>
      <div className="p-4 flex flex-col gap-1.5 flex-1">
        <div className="text-[11px] text-ink-muted flex items-center gap-1"><PinIcon size={11}/>{property.area || property.city}</div>
        <h3 className="font-display text-base leading-snug text-ink line-clamp-2 min-h-[2.6em]">{property.name}</h3>
        {property.rating > 0 && (
          <div className="flex items-center gap-1 text-xs mt-auto pt-1">
            <StarIcon size={12} className="text-sunset-500 fill-current"/>
            <span className="font-medium">{Number(property.rating).toFixed(1)}</span>
            {property.reviewCount > 0 && <span className="text-ink-muted">· {property.reviewCount.toLocaleString()} reviews</span>}
          </div>
        )}
      </div>
    </a>
  );
}

export default function IslandPage({ slug }) {
  const island = ISLANDS[slug];
  const [all, setAll] = useState(null);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("rating");
  const [area, setArea] = useState("");

  useEffect(() => {
    fetch("/data/properties.json")
      .then(r => r.ok ? r.json() : [])
      .then(list => setAll(list.filter(p => p.island === island.name)))
      .catch(() => setAll([]));
  }, [island.name]);

  const areas = useMemo(() => {
    if (!all) return [];
    const seen = new Map();
    for (const p of all) {
      const a = p.area || p.city;
      if (a) seen.set(a, (seen.get(a) || 0) + 1);
    }
    return [...seen.entries()].sort((a, b) => b[1] - a[1]);
  }, [all]);

  const filtered = useMemo(() => {
    if (!all) return [];
    let list = all;
    if (area) list = list.filter(p => (p.area || p.city) === area);
    if (query) {
      const q = query.toLowerCase();
      list = list.filter(p => (p.name || "").toLowerCase().includes(q) || (p.area || "").toLowerCase().includes(q) || (p.city || "").toLowerCase().includes(q));
    }
    const sorted = [...list];
    if (sort === "rating") sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0) || (b.reviewCount || 0) - (a.reviewCount || 0));
    if (sort === "reviews") sorted.sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0));
    if (sort === "name") sorted.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return sorted;
  }, [all, area, query, sort]);

  useEffect(() => { setVisible(PAGE_SIZE); }, [area, query, sort]);

  // JSON-LD: commerce signals — BreadcrumbList + CollectionPage + ItemList of
  // typed Hotel / VacationRental entities. Deliberately NOT Article /
  // BlogPosting — this is a lodging directory on an online travel agency.
  const structuredData = useMemo(() => {
    if (!island) return null;
    const path = `/${slug}/`;
    const list = (all || []).slice(0, 100).map(p => lodgingNode(p, { full: false }));
    return [
      breadcrumbNode([{ name: "Home", path: "/" }, { name: island.display, path }]),
      {
        "@context": CONTEXT,
        "@type": "CollectionPage",
        "@id": absUrl(path),
        url: absUrl(path),
        name: `Hotels & vacation rentals on ${island.display}`,
        description: island.tagline,
        about: { "@type": "Place", name: island.display, containedInPlace: hawaiiPlace() },
        isPartOf: { "@id": WEBSITE_ID },
        provider: { "@id": ORG_ID },
        mainEntity: itemListNode(list, { name: `Places to stay on ${island.display}` })
      }
    ];
  }, [all, slug, island]);
  useJsonLd("island-ldjson", structuredData);
  useHead(island ? {
    title: `${island.display} hotels & vacation rentals | Aloha Rates`,
    description: `${island.tagline} Book ${island.display} hotels, resorts, condos and vacation rentals at real local rates, taxes included.`,
    canonical: `/${slug}/`
  } : { title: "Island not found | Aloha Rates", robots: "noindex" });

  if (!island) return <div className="container-page py-32 text-center"><h1 className="section-title">Island not found</h1></div>;

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[440px] md:min-h-[560px] flex items-end">
        <div className="absolute inset-0 overflow-hidden">
          <img src={island.hero} alt="" className="h-full w-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-b from-ink/30 via-ink/40 to-ink/85"/>
          <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-ink/80 to-transparent"/>
        </div>
        <div className="relative container-page py-10 md:py-14 text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.35)]">
          <nav className="text-xs md:text-sm text-white/90 flex items-center gap-1.5" aria-label="Breadcrumb">
            <a href="/" className="hover:text-white">Home</a>
            <ChevronRightIcon size={12}/>
            <span className="text-white">{island.display}</span>
          </nav>
          <h1 className="mt-4 font-display font-semibold text-4xl md:text-6xl leading-tight !text-white drop-shadow-lg">Stays on {island.display}</h1>
          <p className="mt-3 max-w-2xl text-white text-base md:text-lg">{island.tagline}</p>
          {all && <div className="mt-4 text-sm text-white/90">{all.length.toLocaleString()} properties · direct rates, often better than the big OTAs</div>}
        </div>
      </section>

      {/* Travel facts */}
      <section className="border-b border-ink/5 bg-sand-50">
        <div className="container-page py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {island.facts.map(f => (
              <div key={f.label}>
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">{f.label}</div>
                <div className="mt-1 text-sm md:text-base text-ink font-medium leading-snug">{f.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="container-page pt-10 md:pt-14 max-w-3xl">
        <h2 className="section-title">About {island.display}</h2>
        <p className="prose-body mt-3">{island.intro}</p>
      </section>

      {/* Filters + list */}
      <section className="container-page mt-10 md:mt-12 pb-24">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <h2 className="section-title">All {all ? `${filtered.length.toLocaleString()} ` : ""}properties</h2>
          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"/>
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search by name or area"
                className="pill-input pl-9 pr-3 py-2 text-sm w-56"
              />
            </div>
            <select value={area} onChange={e => setArea(e.target.value)} className="pill-input px-3 py-2 text-sm">
              <option value="">All areas</option>
              {areas.map(([a, n]) => <option key={a} value={a}>{a} ({n})</option>)}
            </select>
            <select value={sort} onChange={e => setSort(e.target.value)} className="pill-input px-3 py-2 text-sm">
              <option value="rating">Top rated</option>
              <option value="reviews">Most reviewed</option>
              <option value="name">Name A–Z</option>
            </select>
          </div>
        </div>

        {all === null ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="card overflow-hidden">
                <div className="aspect-[4/3] bg-sand-200 animate-pulse"/>
                <div className="p-4 space-y-2">
                  <div className="h-3 w-1/3 bg-sand-200 rounded animate-pulse"/>
                  <div className="h-4 w-3/4 bg-sand-200 rounded animate-pulse"/>
                  <div className="h-3 w-1/2 bg-sand-200 rounded animate-pulse"/>
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="card p-10 text-center">
            <p className="prose-body">No matches. Try clearing the filters.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.slice(0, visible).map(p => <PropertyCard key={p.id} property={p}/>)}
            </div>
            {visible < filtered.length && (
              <div className="mt-8 text-center">
                <button onClick={() => setVisible(v => v + PAGE_SIZE)} className="btn-secondary">
                  Show more · {(filtered.length - visible).toLocaleString()} remaining
                </button>
              </div>
            )}
            {/* SEO: emit every property URL as a plain link list so crawlers
                see the full inventory even before "Show more" is clicked. */}
            <ul className="sr-only" aria-hidden="true">
              {filtered.slice(visible).map(p => (
                <li key={p.id}><a href={propertyHref(p)}>{p.name}</a></li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}
